import { CcatExtensionType, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '../../models';
import { LocDisplayType } from '../../models/display-type';
import { SYSTEM_ENUM_ROOT, SYSTEM_ENUM_TYPE } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|CcatExtensionType|`;

type SharedKeys = Pick<LocDef, 'portal' | 'displayType' | 'htmlAllowed' | 'shared' | 'feature'>;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: `${SYSTEM_ENUM_TYPE}` as LocDisplayType,
	htmlAllowed: false,
	shared: true,
	feature: HubFeature.Credential,
};

const sharedSpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'SP',
};

const sharedCpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'CP',
};

export type CPCcatExtensionTypeLocs = `CPCcatExtensionType${CcatExtensionType}`;

export type SPCcatExtensionTypeLocs = `SPCcatExtensionType${CcatExtensionType}`;

export const cpCcatExtensionTypeEnumLocs: LocDefs<CPCcatExtensionTypeLocs> = {
	CPCcatExtensionTypeExtendByUsedItems: {
		defaultValue: 'Extend By Used Items',
		displayPath: `${dpRoot}${CcatExtensionType.ExtendByUsedItems}`,
		description: `System value for '${CcatExtensionType.ExtendByUsedItems}' CCAT extension type`,
		...sharedCpLocData,
	},
	CPCcatExtensionTypeExtendFromInactiveDate: {
		defaultValue: 'Extend From Inactive Date',
		displayPath: `${dpRoot}${CcatExtensionType.ExtendFromInactiveDate}`,
		description: `System value for '${CcatExtensionType.ExtendFromInactiveDate}' CCAT extension type`,
		legacyName: 'Credential/System.Enum.CcatExtensionType.ExtendFromInactiveDate',
		...sharedCpLocData,
	},
	CPCcatExtensionTypeExtendToLogicVersionDate: {
		defaultValue: 'Extend To Logic Version Date',
		displayPath: `${dpRoot}${CcatExtensionType.ExtendToLogicVersionDate}`,
		description: `System value for '${CcatExtensionType.ExtendToLogicVersionDate}' CCAT extension type`,
		...sharedCpLocData,
	},
	CPCcatExtensionTypeIncludeSuspendedTime: {
		defaultValue: 'Include Suspended Time',
		displayPath: `${dpRoot}${CcatExtensionType.IncludeSuspendedTime}`,
		description: `System value for '${CcatExtensionType.IncludeSuspendedTime}' CCAT extension type`,
		legacyName: 'Credential/System.Enum.CcatExtensionType.IncludeSuspendedTime',
		...sharedCpLocData,
	},
	CPCcatExtensionTypeNormal: {
		defaultValue: 'Extend from achievement date',
		displayPath: `${dpRoot}${CcatExtensionType.Normal}`,
		description: `System value for '${CcatExtensionType.Normal}' CCAT extension type`,
		legacyName: 'Credential/System.Enum.CcatExtensionType.Normal',
		...sharedCpLocData,
	},
};

export const spCcatExtensionTypeEnumLocs: LocDefs<SPCcatExtensionTypeLocs> = {
	SPCcatExtensionTypeExtendByUsedItems: {
		defaultValue: 'Extend By Used Items',
		displayPath: `${dpRoot}${CcatExtensionType.ExtendByUsedItems}`,
		description: `System value for '${CcatExtensionType.ExtendByUsedItems}' CCAT extension type`,
		...sharedSpLocData,
	},
	SPCcatExtensionTypeExtendFromInactiveDate: {
		defaultValue: 'Extend From Inactive Date',
		displayPath: `${dpRoot}${CcatExtensionType.ExtendFromInactiveDate}`,
		description: `System value for '${CcatExtensionType.ExtendFromInactiveDate}' CCAT extension type`,
		legacyName: 'Credential/System.Enum.CcatExtensionType.ExtendFromInactiveDate',
		...sharedSpLocData,
	},
	SPCcatExtensionTypeExtendToLogicVersionDate: {
		defaultValue: 'Extend To Logic Version Date',
		displayPath: `${dpRoot}${CcatExtensionType.ExtendToLogicVersionDate}`,
		description: `System value for '${CcatExtensionType.ExtendToLogicVersionDate}' CCAT extension type`,
		...sharedSpLocData,
	},
	SPCcatExtensionTypeIncludeSuspendedTime: {
		defaultValue: 'Include Suspended Time',
		displayPath: `${dpRoot}${CcatExtensionType.IncludeSuspendedTime}`,
		description: `System value for '${CcatExtensionType.IncludeSuspendedTime}' CCAT extension type`,
		legacyName: 'Credential/System.Enum.CcatExtensionType.IncludeSuspendedTime',
		...sharedSpLocData,
	},
	SPCcatExtensionTypeNormal: {
		defaultValue: 'Extend from achievement date',
		displayPath: `${dpRoot}${CcatExtensionType.Normal}`,
		description: `System value for '${CcatExtensionType.Normal}' CCAT extension type`,
		legacyName: 'Credential/System.Enum.CcatExtensionType.Normal',
		...sharedSpLocData,
	},
};

export const CCAT_EXTENSION_TYPE_ENUM_LOCS = MapLocKeys<
	CPCcatExtensionTypeLocs | SPCcatExtensionTypeLocs
>({
	...cpCcatExtensionTypeEnumLocs,
	...spCcatExtensionTypeEnumLocs,
});

export type CPCcatExtensionTypeEnumLocType = LocMap<CPCcatExtensionTypeLocs>;
export type SPCcatExtensionTypeEnumLocType = LocMap<SPCcatExtensionTypeLocs>;

export const cpCcatExtensionTypeKeys: {
	[key in CcatExtensionType]: keyof CPCcatExtensionTypeEnumLocType;
} = {
	[CcatExtensionType.ExtendByUsedItems]:
		CCAT_EXTENSION_TYPE_ENUM_LOCS.CPCcatExtensionTypeExtendByUsedItems,
	[CcatExtensionType.ExtendFromInactiveDate]:
		CCAT_EXTENSION_TYPE_ENUM_LOCS.CPCcatExtensionTypeExtendFromInactiveDate,
	[CcatExtensionType.ExtendToLogicVersionDate]:
		CCAT_EXTENSION_TYPE_ENUM_LOCS.CPCcatExtensionTypeExtendToLogicVersionDate,
	[CcatExtensionType.IncludeSuspendedTime]:
		CCAT_EXTENSION_TYPE_ENUM_LOCS.CPCcatExtensionTypeIncludeSuspendedTime,
	[CcatExtensionType.Normal]: CCAT_EXTENSION_TYPE_ENUM_LOCS.CPCcatExtensionTypeNormal,
};

export const spCcatExtensionTypeKeys: {
	[key in CcatExtensionType]: keyof SPCcatExtensionTypeEnumLocType;
} = {
	[CcatExtensionType.ExtendByUsedItems]:
		CCAT_EXTENSION_TYPE_ENUM_LOCS.SPCcatExtensionTypeExtendByUsedItems,
	[CcatExtensionType.ExtendFromInactiveDate]:
		CCAT_EXTENSION_TYPE_ENUM_LOCS.SPCcatExtensionTypeExtendFromInactiveDate,
	[CcatExtensionType.ExtendToLogicVersionDate]:
		CCAT_EXTENSION_TYPE_ENUM_LOCS.SPCcatExtensionTypeExtendToLogicVersionDate,
	[CcatExtensionType.IncludeSuspendedTime]:
		CCAT_EXTENSION_TYPE_ENUM_LOCS.SPCcatExtensionTypeIncludeSuspendedTime,
	[CcatExtensionType.Normal]: CCAT_EXTENSION_TYPE_ENUM_LOCS.SPCcatExtensionTypeNormal,
};
